import {
  PsvMonitorIneligibilityReason,
  PsvMonitoringProgramStatus,
  PsvSourceFamily,
} from "@api/shared-runtime/psv-monitoring";
import { BadgeCheck, CircleSlash, PauseCircle, ShieldOff } from "lucide-react";

export const SET_UP_STANDARD_LABEL = "Set up standard monitoring";
export const FEATURE_NAME = "Monthly monitoring";
export const ROSTER_MONITORING_LABEL = `${FEATURE_NAME} on`;
export const ROSTER_MONITORING_TOOLTIP =
  "Credentials are re-checked against primary sources every month.";
export const PREVIEW_DEBOUNCE_MS = 400;
export const PREVIEW_PROVIDER_LABEL_LIMIT = 3;
export const SUMMARY_SOURCE_LIMIT = 6;
export const ENABLED_TOAST = "Monitoring is on. Monthly checks start soon.";
export const FORBIDDEN_ERROR_CODE = "FORBIDDEN";
export const CONFLICT_ERROR_CODE = "CONFLICT";

export const MONITORING_COPY = {
  overlap:
    "Each provider gets one monthly check that combines every rule covering them, and is billed once per billing cycle however many rules or pools include them.",
  billing:
    "Billed once per provider each billing cycle while monitoring is on. See your billing plan for pricing.",
  firstChecks: "Checks start soon, then run monthly.",
  reverifiesOnly:
    "Monitoring re-verifies credentials you already hold. It never searches for new licenses.",
  poolAcknowledgement:
    "I understand new members of this pool will be monitored and billed automatically.",
  noBillingPlan:
    "Monthly monitoring needs a billing plan. Contact Saile to set one up.",
  readOnly: "Ask a business unit admin to change monitoring.",
  managedByAdmins: "Monitoring is managed by business unit admins.",
  nationalOnly:
    "Only national sources will be checked. Add a license to the credential file to include it.",
  unnamedPool: "a provider pool",
  firstCheckPending: "First check pending",
  notMonitoredDescription: "Re-check this provider's credentials every month.",
  staleRule:
    "This rule changed since you opened it. Reload to see the latest version.",
  archiveWarning: "Can't be undone. Past results are kept.",
  coversNobody: "Covers nobody",
  memberStops:
    "They'll stop monthly monitoring unless another rule covers them.",
  poolMembersStop:
    "Members will stop monthly monitoring unless another rule covers them.",
  emptyPageReason:
    "Exclusion and sanction lists change monthly. Monitoring re-checks every provider you choose, so you hear about changes without asking.",
  stateRequired: "Choose at least one state.",
  nameRequired: "Name this rule.",
  sourceRequired: "Choose at least one check.",
  targetRequired: "Choose at least one pool or provider.",
  chooseRule: "Rule",
  howBillingWorks: "How billing works",
  turnOn: "Turn on monitoring",
  turnOff: "Turn off",
  manageRules: "Manage rules",
  editorDescription: "Choose what to check and who it covers.",
  discardTitle: "Discard changes?",
  discardDescription: "Your changes haven't been saved.",
  discardConfirm: "Discard",
  discardKeepEditing: "Keep editing",
} as const;

export const INELIGIBILITY_COPY: Record<
  Exclude<
    PsvMonitorIneligibilityReason,
    PsvMonitorIneligibilityReason.NotTargeted
  >,
  string
> = {
  [PsvMonitorIneligibilityReason.FeatureDisabled]:
    "Primary source verification isn't enabled for this business unit.",
  [PsvMonitorIneligibilityReason.AffiliationEnded]:
    "Stopped: this provider is no longer a candidate or hired in this business unit.",
  [PsvMonitorIneligibilityReason.NoCredentialFile]:
    "Nothing to check yet. Monitoring re-checks the credentials on this provider's file here, and none have been added.",
  [PsvMonitorIneligibilityReason.ViewFrozen]:
    "Stopped: this provider's credential file here is frozen.",
};

export const SOURCE_LABELS: Record<PsvSourceFamily, string> = {
  [PsvSourceFamily.StateLicense]: "State licenses",
  [PsvSourceFamily.Dea]: "DEA registration",
  [PsvSourceFamily.Npi]: "NPI registry (NPPES)",
  [PsvSourceFamily.Oig]: "OIG exclusions (LEIE)",
  [PsvSourceFamily.Sam]: "SAM.gov exclusions",
  [PsvSourceFamily.StateMedicaidExclusion]: "State Medicaid exclusions",
  [PsvSourceFamily.Ofac]: "OFAC sanctions",
  [PsvSourceFamily.MedicareOptOut]: "Medicare opt-out",
};

/** Compact labels for one-line summaries. */
export const SOURCE_SHORT_LABELS: Record<PsvSourceFamily, string> = {
  [PsvSourceFamily.StateLicense]: "State licenses",
  [PsvSourceFamily.Dea]: "DEA",
  [PsvSourceFamily.Npi]: "NPI",
  [PsvSourceFamily.Oig]: "OIG",
  [PsvSourceFamily.Sam]: "SAM",
  [PsvSourceFamily.StateMedicaidExclusion]: "Medicaid exclusions",
  [PsvSourceFamily.Ofac]: "OFAC",
  [PsvSourceFamily.MedicareOptOut]: "Medicare opt-out",
};

export const SOURCE_GROUPS: {
  name: string;
  families: readonly PsvSourceFamily[];
}[] = [
  {
    name: "Licenses and registrations",
    families: [
      PsvSourceFamily.StateLicense,
      PsvSourceFamily.Dea,
      PsvSourceFamily.Npi,
    ],
  },
  {
    name: "Exclusions and sanctions",
    families: [
      PsvSourceFamily.Oig,
      PsvSourceFamily.Sam,
      PsvSourceFamily.StateMedicaidExclusion,
      PsvSourceFamily.Ofac,
    ],
  },
  { name: "Medicare", families: [PsvSourceFamily.MedicareOptOut] },
];

export const STATE_SCOPE_HINTS = {
  [PsvSourceFamily.StateLicense]:
    "All states means every state where the provider has a saved license.",
  [PsvSourceFamily.StateMedicaidExclusion]:
    "All states means every supported state, including ones added later.",
} as const;

export const ALL_SOURCE_FAMILIES: readonly PsvSourceFamily[] =
  Object.values(PsvSourceFamily);

export enum MonitoringBadgeKind {
  Monitored = "monitored",
  Paused = "paused",
  NotMonitored = "not-monitored",
  Unavailable = "unavailable",
  Archived = "archived",
}

/** Red is reserved for verification findings, so no state here uses it. */
export const BADGE_PRESENTATION = {
  [MonitoringBadgeKind.Monitored]: {
    label: "Monitored",
    icon: BadgeCheck,
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  [MonitoringBadgeKind.Paused]: {
    label: "Paused",
    icon: PauseCircle,
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  [MonitoringBadgeKind.NotMonitored]: {
    label: "Not monitored",
    icon: CircleSlash,
    className: "text-muted-foreground",
  },
  [MonitoringBadgeKind.Unavailable]: {
    label: "Unavailable",
    icon: ShieldOff,
    className: "text-muted-foreground",
  },
  [MonitoringBadgeKind.Archived]: {
    label: "Archived",
    icon: CircleSlash,
    className: "text-muted-foreground",
  },
} as const;

export const PROGRAM_STATUS_BADGE: Record<
  PsvMonitoringProgramStatus,
  MonitoringBadgeKind
> = {
  [PsvMonitoringProgramStatus.Active]: MonitoringBadgeKind.Monitored,
  [PsvMonitoringProgramStatus.Paused]: MonitoringBadgeKind.Paused,
  [PsvMonitoringProgramStatus.Archived]: MonitoringBadgeKind.Archived,
};

/** Rule status is labelled "Active" on the rules table, not "Monitored". */
export const PROGRAM_STATUS_LABEL: Record<PsvMonitoringProgramStatus, string> =
  {
    [PsvMonitoringProgramStatus.Active]: "Active",
    [PsvMonitoringProgramStatus.Paused]: "Paused",
    [PsvMonitoringProgramStatus.Archived]: "Archived",
  };

export function standardPausedMessage(ruleName: string): string {
  return `${ruleName} is paused. Resume it in Credentials › Settings to turn monitoring on.`;
}
